import { useMemo, useState } from 'react'
import { BellRing, Check, ChevronLeft, ChevronRight, Clock3, Coffee, MoonStar, Sparkles } from 'lucide-react'
import type { SettingsDto } from '../types'
import { haptic } from '../lib/telegram'
import { useDialogAccessibility } from '../hooks/useDialogAccessibility'

interface Props {
  initial: SettingsDto
  firstName?: string
  onComplete: (settings: SettingsDto) => Promise<void>
  onError: (message: string) => void
}

export default function Onboarding({ initial, firstName, onComplete, onError }: Props) {
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const dialogRef = useDialogAccessibility<HTMLElement>()
  const [form, setForm] = useState<SettingsDto>({
    ...initial,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || initial.timeZone,
  })

  const progress = useMemo(() => `${((step + 1) / 3) * 100}%`, [step])
  const validTarget = Number.isInteger(form.targetMinutes) && form.targetMinutes >= 1 && form.targetMinutes <= 1440

  const setWeekdayTarget = (minutes: number) => setForm((current) => ({
    ...current,
    targetMinutes: minutes,
    weekdayTargets: { ...current.weekdayTargets, ...Object.fromEntries(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'].map((day) => [day, minutes])) },
  }))

  const update = <K extends keyof SettingsDto>(key: K, value: SettingsDto[K]) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const finish = async () => {
    if (saving || !validTarget) return
    setSaving(true)
    try {
      await onComplete(form)
      haptic('success')
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Не удалось сохранить настройки')
      haptic('error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="onboarding-backdrop">
      <section className="onboarding-card" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Первоначальная настройка" tabIndex={-1}>
        <div className="onboarding-progress"><span style={{ width: progress }} /></div>
        <div className="onboarding-step">Шаг {step + 1} из 3</div>

        {step === 0 && (
          <div className="onboarding-content">
            <div className="onboarding-icon purple"><Sparkles size={30} /></div>
            <h1>{firstName ? `${firstName}, настроим ваш день` : 'Настроим ваш день'}</h1>
            <p>Выберите норму для будней. Позже можно настроить каждый день недели отдельно, а цель на сегодня — изменить на главной.</p>
            <span className="field-label">Рабочая норма</span>
            <div className="target-picker">
              {[420, 480, 540].map((minutes) => (
                <button key={minutes} className={form.targetMinutes === minutes ? 'selected' : ''}
                  aria-pressed={form.targetMinutes === minutes} onClick={() => setWeekdayTarget(minutes)}>
                  {minutes / 60} часов
                  {form.targetMinutes === minutes && <Check size={16} />}
                </button>
              ))}
            </div>
            <div className="inline-field">
              <Clock3 size={19} />
              <label htmlFor="onboarding-target">Своя норма, минут</label>
              <input id="onboarding-target" type="number" min={1} max={1440} value={form.targetMinutes}
                onChange={(event) => setWeekdayTarget(Number(event.target.value))} />
            </div>
            {!validTarget && <p className="modal-error" role="alert">Укажите норму от 1 минуты до 24 часов.</p>}
            <div className="timezone-note"><MoonStar size={17} /> Часовой пояс: {form.timeZone}</div>
          </div>
        )}

        {step === 1 && (
          <div className="onboarding-content">
            <div className="onboarding-icon amber"><Coffee size={30} /></div>
            <h1>Какие перерывы нужны?</h1>
            <p>Включите только те кнопки, которыми будете пользоваться. Обед уже включён.</p>
            <div className="choice-list">
              <ToggleRow label="Завтрак" detail="Перерыв внутри рабочей сессии" checked={form.breakfastEnabled}
                onChange={(value) => update('breakfastEnabled', value)} />
              <ToggleRow label="Обед" detail="Включён по умолчанию" checked={form.lunchEnabled}
                onChange={(value) => update('lunchEnabled', value)} />
              <ToggleRow label="Ужин" detail="Для длинных и ночных смен" checked={form.dinnerEnabled}
                onChange={(value) => update('dinnerEnabled', value)} />
              <ToggleRow label="Отвлечения" detail="Встречи, личные дела и паузы" checked={form.distractionEnabled}
                onChange={(value) => update('distractionEnabled', value)} />
              <ToggleRow label="Курение" detail="Отдельная статистика перекуров" checked={form.smokingEnabled}
                onChange={(value) => update('smokingEnabled', value)} />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-content">
            <div className="onboarding-icon green"><BellRing size={30} /></div>
            <h1>Учёт и напоминания</h1>
            <p>Выберите паузы, которые нужно вычитать из рабочего времени. Они будут сдвигать прогноз ухода.</p>
            <div className="choice-list compact">
              {form.breakfastEnabled && <ToggleRow label="Вычитать завтрак" checked={form.excludeBreakfast}
                onChange={(value) => update('excludeBreakfast', value)} />}
              {form.lunchEnabled && <ToggleRow label="Вычитать обед" checked={form.excludeLunch}
                onChange={(value) => update('excludeLunch', value)} />}
              {form.dinnerEnabled && <ToggleRow label="Вычитать ужин" checked={form.excludeDinner}
                onChange={(value) => update('excludeDinner', value)} />}
              {form.distractionEnabled && <ToggleRow label="Вычитать отвлечения" checked={form.excludeDistraction}
                onChange={(value) => update('excludeDistraction', value)} />}
              {form.smokingEnabled && <ToggleRow label="Вычитать перекуры" checked={form.excludeSmoking}
                onChange={(value) => update('excludeSmoking', value)} />}
              <ToggleRow label="Напоминания" detail="О цели и перерывах — интервалы можно изменить в настройках"
                checked={form.remindersEnabled} onChange={(value) => update('remindersEnabled', value)} />
              <ToggleRow label="Ежедневный отчёт" checked={form.dailyReportEnabled}
                onChange={(value) => update('dailyReportEnabled', value)} />
              <ToggleRow label="Еженедельный отчёт" checked={form.weeklyReportEnabled}
                onChange={(value) => update('weeklyReportEnabled', value)} />
            </div>
          </div>
        )}

        <footer className="onboarding-footer">
          {step > 0 ? (
            <button className="button ghost" onClick={() => setStep(step - 1)}><ChevronLeft size={19} /> Назад</button>
          ) : <span />}
          {step < 2 ? (
            <button className="button primary" disabled={!validTarget} onClick={() => { setStep(step + 1); haptic('select') }}>
              Продолжить <ChevronRight size={19} />
            </button>
          ) : (
            <button className="button primary" disabled={saving} onClick={finish}>
              <Check size={19} /> {saving ? 'Сохраняю…' : 'Готово'}
            </button>
          )}
        </footer>
      </section>
    </div>
  )
}

export function ToggleRow({ label, detail, checked, onChange }: {
  label: string; detail?: string; checked: boolean; onChange: (value: boolean) => void
}) {
  return (
    <label className="toggle-row">
      <span><b>{label}</b>{detail && <small>{detail}</small>}</span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <i aria-hidden><span /></i>
    </label>
  )
}
